import { Badge } from '@ipfabric/ui-components/Badge/Badge';
import { Link } from '@ipfabric/ui-components/Link/Link';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import type { FormSnippet } from '@ipfabric/ui-components/utils/formSnippets/FormSnippet';
import { NoCredentialsBox } from 'components/NoCredentialsBox/NoCredentialsBox';
import { type UseFormReturn, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { useAuthenticationValidationContext } from '../components/Authentication/context/AuthenticationValidationContext';
import VendorsApi from '../components/VendorsApi/VendorsApi';
import { transformVendorsForRequest } from '../components/VendorsApi/helpers/transformVendorsForRequest';
import type { ExistingVendor } from '../components/VendorsApi/vendorTypes';
import { useDiscoverySettingsContext } from '../composition/DiscoverySettings/discoverySettingsContext';
import type { DiscoverySettingsObject } from '../model/DiscoverySettingsObject';

export type VendorsApiFieldValues = {
  vendors: ExistingVendor[];
};
export type VendorsApiOutputValues = {
  vendors: ExistingVendor[];
};

const VendorsApiFormAdapter = (props: {
  form: UseFormReturn<VendorsApiFieldValues, unknown, VendorsApiOutputValues>;
}) => {
  const { newSnapshot, showUpdateFromGlobalSettingsButton, snapshotId } =
    useDiscoverySettingsContext();
  const vendors = useWatch({
    control: props.form.control,
    name: 'vendors',
  });

  const authenticationValidation = useAuthenticationValidationContext();

  const noCredentialBox = authenticationValidation.hasError ? (
    <NoCredentialsBox
      title='No stored vendor APIs'
      icon='fa-exclamation-circle'
      type='error'
      className='-mx-2 -mt-2 border-t-0'
    >
      At least one Vendor API or{' '}
      <Link
        renderAs='button'
        onClick={() => {
          authenticationValidation.setSubTab('deviceCredentials');
        }}
      >
        Device credential
      </Link>{' '}
      is required to run a discovery.
    </NoCredentialsBox>
  ) : (
    <NoCredentialsBox
      title='No stored vendor APIs'
      icon='fa-info-circle'
      type='info'
      className='-mx-2 -mt-2 border-t-0'
    />
  );

  const renderTitleBadge = () => {
    if (authenticationValidation.hasError) {
      return (
        <Tooltip content={authenticationValidation.errorLong}>
          <Badge
            variant='plain'
            color='error'
            icon='fa-circle-xmark'
            className='ml-4 cursor-default'
          >
            {authenticationValidation.errorShort}
          </Badge>
        </Tooltip>
      );
    }
    return undefined;
  };

  return (
    <VendorsApi
      newSnapshot={newSnapshot}
      noCredentialBox={noCredentialBox}
      onVendorsChange={(vendors) => {
        props.form.setValue('vendors', vendors, { shouldDirty: true });
      }}
      showUpdateFromGlobalSettingsButton={showUpdateFromGlobalSettingsButton}
      snapshotId={snapshotId}
      vendors={vendors}
      renderTitleBadge={renderTitleBadge}
    />
  );
};

export const storedVendorsApi: FormSnippet<
  DiscoverySettingsObject,
  VendorsApiFieldValues,
  VendorsApiOutputValues
> = {
  render: ({ form }) => <VendorsApiFormAdapter form={form} />,

  fromModel: (model, fieldValuesDraft) => {
    fieldValuesDraft.vendors = Array.isArray(model.vendorApi)
      ? model.vendorApi
      : [];
  },

  toModel: (transformedValues, modelDraft) => {
    modelDraft.vendorApi = transformVendorsForRequest(
      transformedValues.vendors || [],
    );
  },

  schema: z.object({
    vendors: z.any(),
  }) as z.Schema<{ vendors: ExistingVendor[] }>,
};
